"use client"

import { useQuery, useQueryClient } from "@tanstack/react-query"
import { getCollections } from "../api"
import { useAuthStore } from "@/store/useAuthStore"
import type { CollectionsQueryParams, CollectionsResponse } from "../types"

export function useCollections(
  params: CollectionsQueryParams,
  options?: { initialData?: CollectionsResponse; enabled?: boolean }
) {
  const key = [
    "collections",
    params.page ?? 1,
    params.perPage ?? 15,
    params.search ?? "",
    params.status ?? "",
    params.network_id ?? "",
    params.category_id ?? "",
    params.orderBy ?? "",
    params.order ?? "",
    params.range ?? "",
    params.trend ?? "",
    params.topCollection ?? "",
    params.watchlist ?? "",
    params.isSpecial ?? "",
  ]

  // `order` joins this test in Phase 04: the shared sort control can select
  // `id asc` ("oldest first"), which changes the result set while leaving
  // `orderBy` at its default — so a guard reading `orderBy` alone would seed an
  // ascending query with the server's descending page.
  const hasFilters = !!params.search || !!params.network_id || !!params.category_id || !!params.status || (params.orderBy && params.orderBy !== 'id') || (params.order && params.order !== 'desc') || !!params.trend || !!params.watchlist || !!params.range || !!params.isSpecial;

  // A shaped placeholder from a failed server fetch is never seeded: React Query
  // would report success with zero rows and the list would render "no
  // collections" for a failure (D049).
  const isFallbackPayload = options?.initialData?.message === "offline_fallback"

  const initialForKey =
    !hasFilters &&
    !isFallbackPayload &&
    options?.initialData &&
    (params.page ?? 1) === options.initialData.data.page.current_page &&
    // The page size too: a grid sized to its columns (lib/grid-rows) asks for
    // another size, and the server's page must not stand in for it.
    (params.perPage ?? 15) === Number(options.initialData.data.page.per_page)
      ? options.initialData
      : undefined

  const { token } = useAuthStore()
  const queryClient = useQueryClient()

  const query = useQuery<CollectionsResponse>({
    queryKey: key,
    queryFn: () => getCollections(params),
    enabled: options?.enabled !== false,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    placeholderData: (previous) => previous,
    initialData: initialForKey,
    // The public landing page seeds this from `publicServerFetch`, which never
    // reads the token cookie (deliberately — reading it would opt the route out
    // of static rendering). That payload therefore has `current_user.like` /
    // `.watchlist` false for everyone, and untouched initialData counts as
    // fresh for the full staleTime, so a signed-in user saw their own saved
    // collections rendered unsaved for a minute. Backdating it to 0 keeps the
    // instant first paint but refetches under the user's token right away.
    initialDataUpdatedAt: initialForKey && token ? 0 : undefined,
  })

  return {
    ...query,
    /**
     * A retry that actually re-requests. `refetch()` was measured to be a no-op
     * on the state a failed browse request leaves behind (D049 recorded the same
     * on Home), so the control resets the query instead.
     */
    reset: () => queryClient.resetQueries({ queryKey: key }),
  }
}
